import { ArrowDownOutlined, FieldTimeOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { addCommasToNumber, BBDTag, Texto } from '@gravitate-js/excalibrr'
import { Button, Col, Popover, Row, Tooltip } from 'antd'
import moment from 'moment'
import React from 'react'
import { formatters } from '@utils/intl/dateTimeFormatters'

export const getColumnDefs = (toggleDrawer: (data) => void | null) => [
  {
    headerName: '',
    field: 'price_effective_from',
    maxWidth: 50,
    cellRenderer: ({ data }) => {
      if (toggleDrawer) {
        return (
          <Tooltip title='View Directive History'>
            <Button
              icon={<FieldTimeOutlined />}
              style={{ float: 'right', marginRight: '1em', color: 'var(--theme-color-1)' }}
              type='link'
              onClick={() => toggleDrawer(data)}
            />
          </Tooltip>
        )
      }

      return null
    },
  },
  {
    field: 'supplier',
    headerName: 'Supplier',
    minWidth: 200,
    cellRenderer: ({ value }) => {
      return <span>{value}</span>
    },
  },
  {
    field: 'product',
    minWidth: 240,
    headerName: 'Product',
    cellRenderer: ({ value, data }) => {
      if (data.details && data.details.length > 1) {
        return <GroupingPopover row={data} />
      }
      return <b>{value}</b>
    },
  },
  {
    field: 'terminal',
    minWidth: 140,
    headerName: 'Terminal',
    cellRenderer: ({ value }) => {
      return <b>{value}</b>
    },
  },
  {
    field: 'price_type',
    headerName: 'Price Type',
    minWidth: 80,
    cellRenderer: ({ value }) => {
      return <span>{value.toUpperCase()}</span>
    },
  },
  {
    field: 'contract',
    headerName: 'Contract',
    minWidth: 150,
    cellRenderer: ({ value }) => {
      return value && value !== '' && <BBDTag className='theme-1-gray-tag'>{value}</BBDTag>
    },
  },
  {
    field: 'price',
    headerName: 'Price',
    minWidth: 150,
    cellRenderer: ({ value, data }) => {
      if (data.has_no_curves) return null
      const price = value ? value.toFixed(4) : 'multiple'
      return <SupplyPriceTag price={price} deltaToLow={data.delta_to_low} className='report-tag' />
    },
  },
  {
    field: 'directive_effective_from',
    headerName: 'Effective From',
    minWidth: 100,
    valueGetter: (params) =>
      formatters.DATE_TIME(moment(params.data.directive_effective_from), params.data.directive_effective_from_local),
  },
  {
    field: 'min_quantity',
    headerName: 'Min QTY',
    minWidth: 60,
    cellRenderer: ({ value }) => (value && value !== '' ? addCommasToNumber(value) : ''),
  },
  {
    field: 'max_quantity',
    headerName: 'Max QTY',
    minWidth: 60,
    cellRenderer: ({ value }) => (value && value !== '' ? addCommasToNumber(value) : ''),
  },
  {
    field: 'loaded',
    type: 'rightAligned',
    minWidth: 60,
    cellRenderer: ({ value }) => <b>{addCommasToNumber(value.toFixed(0))}</b>,
  },
  {
    field: 'scheduled',
    type: 'rightAligned',
    minWidth: 60,
    cellRenderer: ({ value }) => <b>{addCommasToNumber(value.toFixed(0))}</b>,
  },
  {
    field: 'planned',
    type: 'rightAligned',
    minWidth: 60,
    cellRenderer: ({ value }) => <b>{addCommasToNumber(value.toFixed(0))}</b>,
  },
  {
    field: 'status',
    minWidth: 150,
    cellRenderer: ({ value, data }) => {
      return value && value.toLowerCase() === 'under' ? (
        <BBDTag error className='report-tag'>
          <span className='under-value'>
            <ArrowDownOutlined />
          </span>
          {data.status_value && addCommasToNumber(parseInt(data.status_value))} {value}
        </BBDTag>
      ) : value === 'over' ? (
        <BBDTag error className='report-tag'>
          {value} ({data.status_value && addCommasToNumber(parseInt(data.status_value))})
        </BBDTag>
      ) : (
        <BBDTag success className='report-tag'>
          {value}
        </BBDTag>
      )
    },
  },
]

const GroupingPopover = ({ row }) => {
  const content = (
    <div style={{ fontSize: '0.8em', width: '800px' }}>
      <Row
        className='header p-2 pt-3 pl-3 pr-3'
        style={{ width: '100%', display: 'flex', justifyContent: 'center', borderBottom: '1px solid var(--gray-200)' }}
      >
        <Col span={4}>Terminal</Col>
        <Col span={4}>Product</Col>
        <Col span={4} style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
          Loaded
        </Col>
        <Col span={4} style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
          Scheduled
        </Col>
        <Col className='pr-3' span={4} style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
          Planned
        </Col>
        <Col style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }} span={4}>
          Price
        </Col>
      </Row>
      {row.details.map((c, i) => (
        <Row key={i} className='row p-2 pt-3 pl-3 pr-3'>
          <Col span={4}>
            <b>{c.terminal}</b>
          </Col>
          <Col
            className='lighter-color'
            style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
            span={4}
          >
            <b>{c.product}</b>
          </Col>
          <Col
            span={4}
            className='pl-1'
            style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
          >
            <b>{c.loaded}</b>
          </Col>
          <Col
            span={4}
            className='pl-1'
            style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
          >
            <b>{c.scheduled}</b>
          </Col>
          <Col
            span={4}
            className='pl-1'
            style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
          >
            <b>{c.planned}</b>
          </Col>
          <Col
            span={4}
            className='pr-2'
            style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
          >
            <SupplyPriceTag price={c.price} deltaToLow={c.delta_to_low} className='mb-2 report-tag popover' />
          </Col>
        </Row>
      ))}
    </div>
  )

  return (
    <Popover
      content={content}
      title={
        <div
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid var(--gray-200)',
            marginBottom: '8px',
          }}
        >
          <Texto weight='bold' appearance='medium' category='h5'>
            Directive Grouping
          </Texto>
        </div>
      }
      placement='right'
    >
      <div>
        <BBDTag theme2 className='report-tag'>
          <>
            <InfoCircleOutlined className='pr-1' />
            <span className='pr-2'>{row.product}</span>
          </>
        </BBDTag>
      </div>
    </Popover>
  )
}

function SupplyPriceTag({ price, deltaToLow, className }) {
  if (price === 'multiple')
    return (
      <BBDTag className={className} success>
        Multiple
      </BBDTag>
    )

  const { formatCurrency } = useFormatter()
  const formattedPrice = formatCurrency(parseFloat(price), true, 4)

  return deltaToLow > 0 ? (
    <BBDTag className={className} error>{`${formattedPrice} (+${deltaToLow.toFixed(4)})`}</BBDTag>
  ) : (
    <BBDTag className={className} success>{`${formattedPrice} (Low)`}</BBDTag>
  )
}
